import "@diligentcorp/atlas-web-component-icons/lib/close/index.js";
import "@diligentcorp/atlas-web-component-icons/lib/expand-down/index.js";
import "@diligentcorp/atlas-web-component-icons/lib/menu/index.js";
import "@diligentcorp/atlas-web-component-icons/lib/search/index.js";
import "@diligentcorp/atlas-web-component-icons/lib/building/index.js";
import { msg, str, updateWhenLocaleChanges } from "@lit/localize";
import { css, html, LitElement } from "lit";
import { classMap } from "lit/directives/class-map.js";
import { map } from "lit/directives/map.js";
import { when } from "lit/directives/when.js";
import { CHANGE_ORG_EVENT_NAME, MINIMUM_ORG_COUNT_SEARCH } from "../configuration/constants.js";
import { defineComponent, dispatchToggleModalEvent } from "../configuration/utils.js";
import { FocusTrapController } from "../controllers/FocusTrapController.js";
import { buttonDefaults, coreVar, iconButton, mediumButton, secondaryButton, semanticVar, tertiaryButton, } from "./styles.js";
const DIALOG_ID = "atlas-gn-org-switcher-dialog";
const DIALOG_TITLE_ID = `${DIALOG_ID}-title`;
const SEARCH_ID = `${DIALOG_ID}-search`;
const spacingVar = (name) => coreVar(`spacing-${name}`);
/**
 * The `atlas-gn-org-switcher` component displays a button that opens a dialog with a list of organizations.
 * The user can select an organization to switch to. The component is only visible if the user is a member of more than one organization.
 *
 * @tag atlas-gn-org-switcher
 * @prop {number} initialOrganizationId - The id of the organization that is currently selected.
 * @prop {Organization[]} organizations - The list of organizations the user is a member of.
 * @fires {CustomEvent<{ orgId: number; orgUrl: string }>} change-org - Fired when the user selects an organization.
 */
export class OrgSwitcher extends LitElement {
    constructor() {
        super();
        this.focusTrap = new FocusTrapController(this, () => this.hideDialog());
        this.showDialog = () => {
            this._isOpen = true;
            dispatchToggleModalEvent(this, true, "dialog", this.hideDialog);
        };
        this.hideDialog = () => {
            this._isOpen = false;
            dispatchToggleModalEvent(this, false, "dialog");
        };
        this.toggleDialog = () => {
            if (this._isOpen) {
                this.hideDialog();
            }
            else {
                this.showDialog();
            }
        };
        this.renderDialog = () => {
            return html `
      <div
        class="dialog"
        id=${DIALOG_ID}
        aria-modal="true"
        aria-labelledby=${DIALOG_TITLE_ID}
        tabindex="-1"
      >
        ${when(this.currentOrg, this.renderHeader)}
        <main tabindex="-1">
          <div class="header">
            <span>${msg("Switch organization")}</span>
            ${this.renderSearch()}
          </div>
          ${this.renderList()} ${this.renderFooter()}
        </main>
      </div>
    `;
        };
        this.renderButton = () => {
            if (!this.currentOrg) {
                return html ``;
            }
            if (this.isMultiOrg) {
                return html `
        <button
          class=${classMap({
                    tertiary: true,
                    medium: true,
                    open: this._isOpen,
                })}
          @click=${this.toggleDialog}
          aria-label=${msg(`Open organization switcher dialog`)}
          aria-haspopup="dialog"
          aria-expanded=${this._isOpen ? "true" : "false"}
          aria-controls=${DIALOG_ID}
        >
          ${this.renderOrgLogo(this.currentOrg)}
          <span>${this.currentOrg.name ?? msg(`Switch organization`)}</span>
          <dil-expand-down-icon aria-hidden="true"></dil-expand-down-icon>
        </button>
      `;
            }
            else {
                return html `
        <button class="tertiary medium" disabled>
          ${this.renderOrgLogo(this.currentOrg)}
          <span>${this.currentOrg.name ?? msg(`Switch organization`)}</span>
        </button>
      `;
            }
        };
        this.renderOrgLogo = (org) => {
            if (!org || !org.logo) {
                return html `
        <div class="logo placeholder">
          <dil-building-icon aria-hidden="true"></dil-building-icon>
        </div>
      `;
            }
            return html `<img src=${org.logo} alt=${org.name} aria-hidden="true" class="logo" />`;
        };
        this.renderHeader = (org) => {
            return html `
      <header>
        <div class="current-org">
          ${this.renderOrgLogo(org)}
          <span>${org.name}</span>
        </div>
      </header>
    `;
        };
        this.renderSearch = () => {
            const needsSearch = this.organizations.length >= MINIMUM_ORG_COUNT_SEARCH;
            if (!needsSearch) {
                return html ``;
            }
            return html `
      <label for=${SEARCH_ID}> ${msg("Search")} </label>
      <div class="search">
        <input
          type="search"
          placeholder=${msg("Search organizations")}
          aria-label=${msg("Search organizations")}
          id=${SEARCH_ID}
          .value=${this._search}
          @input=${(e) => {
                this._search = e.target.value;
            }}
        />
        <dil-search-icon aria-hidden="true"></dil-search-icon>
      </div>
    `;
        };
        this.renderList = () => {
            const hasNoResults = this.filteredOrgs.length === 0 && this._search.length > 0;
            return html `
      <ul>
        ${map(this.filteredOrgs, this.renderListItem)}
        ${when(hasNoResults, this.renderNoResultsNotice)}
      </ul>
    `;
        };
        this.renderListItem = (org) => {
            return html `
      <li>
        <button
          @click=${() => this.openOrg(org)}
          aria-label=${msg(str `Switch to ${org.name}`, {
                desc: "Accessibility label for switching to a specific organization",
            })}
          tabindex="0"
        >
          ${this.renderOrgLogo(org)}
          <span>${org.name}</span>
          <dil-expand-right-icon aria-hidden="true"></dil-expand-right-icon>
        </button>
      </li>
    `;
        };
        this.renderNoResultsNotice = () => {
            return html `<li class="no-results" role="alert">${msg("No organizations found")}</li>`;
        };
        updateWhenLocaleChanges(this);
        this.organizations = [];
        this._search = "";
        this._isOpen = false;
    }
    get currentOrg() {
        if (this.initialOrganizationId) {
            return this.organizations.find((org) => org.id === this.initialOrganizationId);
        }
    }
    get isMultiOrg() {
        return this.organizations.length > 1;
    }
    get filteredOrgs() {
        const search = this._search.toLowerCase();
        return this.organizations
            .filter((org) => org.name.toLowerCase().includes(search))
            .filter((org) => !this.initialOrganizationId || org.id !== this.initialOrganizationId)
            .sort((a, b) => (a.customerName && b.customerName && a.customerName.localeCompare(b.customerName)) ||
            a.name.localeCompare(b.name));
    }
    updated(_oldProps) {
        const wasOpen = _oldProps.get("_isOpen");
        if (!wasOpen && this._isOpen) {
            this.focusTrap.activate();
        }
        else if (wasOpen && !this._isOpen) {
            this.focusTrap.deactivate();
        }
    }
    render() {
        return html `${this.renderButton()}${when(this._isOpen, this.renderDialog)}`;
    }
    renderFooter() {
        return html `
      <div class="footer">
        <div class="actions">
          <button
            class="secondary medium"
            @click=${this.hideDialog}
            aria-label=${msg(`Close`)}
            aria-controls=${DIALOG_ID}
            tabindex="0"
          >
            ${msg(`Close`)}
          </button>
        </div>
      </div>
    `;
    }
    openOrg(org) {
        this.dispatchEvent(new CustomEvent(CHANGE_ORG_EVENT_NAME, {
            detail: { orgId: org.id, orgUrl: org.launchpad },
            bubbles: true,
            composed: true,
        }));
    }
}
OrgSwitcher.properties = {
    initialOrganizationId: { type: Number, reflect: true, attribute: "initial-organization-id" },
    organizations: { type: Array, reflect: false, attribute: "organizations" },
    _search: { state: true },
    _isOpen: { state: true },
};
OrgSwitcher.styles = [
    buttonDefaults,
    secondaryButton,
    tertiaryButton,
    mediumButton,
    iconButton,
    css `
      :host {
        display: flex;
        position: relative;
      }
      :host > button {
        overflow: hidden;
        height: ${spacingVar("6")};
        padding-left: ${spacingVar("1-5")};
        padding-right: ${spacingVar("1-5")};
        gap: ${spacingVar("1-5")};
        background-color: transparent;
      }
      :host > button .logo {
        width: ${spacingVar("4")};
        height: ${spacingVar("4")};
        border-radius: ${semanticVar("radius-md")};
      }
      :host > button span {
        flex-grow: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      :host > button.open {
        box-shadow: inset 0px 0px 0px 1px ${semanticVar("color-action-secondary-outline")};
        background-color: ${semanticVar("color-action-secondary-active-fill")};
      }

      .dialog {
        display: none;
        position: absolute;
        z-index: calc(Infinity);
        top: calc(100% + ${spacingVar("0-5")});
        left: 0;
        flex-direction: column;
        padding: 0;
        border: none;
        background-color: ${semanticVar("color-background-base")};
        color: ${semanticVar("color-type-default")};
        border-radius: ${semanticVar("radius-lg")};
        animation: slide-up var(--atlas-global-nav-animation-duration)
          cubic-bezier(0.33, 1, 0.68, 1);
        box-shadow: ${coreVar("shadow-high")};
        display: flex;
        width: 480px;
        box-sizing: border-box;
        max-height: calc(100svh - 74px);
        overflow: hidden;
      }

      header {
        position: sticky;
        top: 0;
        display: flex;
        color: ${semanticVar("color-type-default")};
        font-size: ${semanticVar("font-text-body-font-size")};
        font-weight: ${semanticVar("font-weight-emphasis")};
        line-height: ${semanticVar("font-text-body-line-height")};
        padding: ${spacingVar("1-5) var(--lens-core-spacing-2")};
        border-bottom: 1px solid ${semanticVar("color-ui-divider-default")};
        border-top-left-radius: inherit;
        border-top-right-radius: inherit;
      }

      header span {
        font-size: ${semanticVar("font-text-md-font-size")};
        font-weight: ${semanticVar("font-weight-emphasis")};
        line-height: ${semanticVar("font-text-md-line-height")};
        letter-spacing: ${semanticVar("font-text-md-letter-spacing")};
      }

      header .current-org {
        display: flex;
        align-items: center;
        align-items: center;
        gap: ${spacingVar("3")};
        padding-left: ${spacingVar("1")};
        padding-top: ${spacingVar("1-5")};
        padding-bottom: ${spacingVar("1-5")};
      }

      main {
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        position: relative;
        background-color: inherit;
      }

      main > .header {
        display: flex;
        flex-direction: column;
        padding-left: ${spacingVar("2")};
        padding-right: ${spacingVar("2")};
        padding-top: ${spacingVar("1-5")};
        font-size: ${semanticVar("font-text-body-font-size")};
        font-weight: ${semanticVar("font-weight-emphasis")};
        line-height: ${semanticVar("font-text-body-line-height")};
        letter-spacing: ${semanticVar("font-text-body-letter-spacing")};
      }

      main > .header > span:has(+ label) {
        padding-bottom: ${spacingVar("1-5")};
      }

      main > .header .search {
        display: grid;
        grid-template-columns: min-content 1fr;
      }

      main > .header label {
        padding-bottom: ${spacingVar("1")};
        font-size: ${semanticVar("font-text-sm-font-size")};
        font-weight: ${semanticVar("font-weight-emphasis")};
        line-height: ${semanticVar("font-text-sm-line-height")};
        letter-spacing: ${semanticVar("font-text-sm-letter-spacing")};
      }

      main > .header .search input {
        grid-column: 1 / -1;
        grid-row: 1;
      }

      main > .header .search dil-search-icon {
        grid-column: 1;
        grid-row: 1;
        align-items: center;
        padding-left: ${spacingVar("1-5")};
      }

      header {
        background-color: inherit;
      }
      main .footer {
        display: flex;
        flex-direction: column;
        position: sticky;
        bottom: 0;
        padding-left: ${spacingVar("2")};
        padding-right: ${spacingVar("2")};
        background-color: ${semanticVar("color-background-base")};
      }
      main .footer .actions {
        display: flex;
        flex-direction: column;
        padding-top: ${spacingVar("3")};
        padding-bottom: ${spacingVar("1-5")};
        box-shadow: 0px -1px 0px ${semanticVar("color-ui-divider-default")};
      }
      main .footer button {
        justify-content: center;
        z-index: 1;
      }

      @media (max-width: 600px) {
        .dialog {
          position: fixed;
          bottom: 0;
          top: unset;
          width: 100vw;
          max-width: 100vw;
          max-height: calc(100svh - ${spacingVar("2")});
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          overflow-y: auto;
        }
        .dialog::before {
          content: "";
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-color: #282e3780;
          animation-name: fade-in;
          animation-duration: var(--atlas-global-nav-animation-duration);
          animation-timing-function: var(--atlas-global-nav-easing);
        }
        :host > button {
          padding-left: ${spacingVar("0-5")};
          padding-right: ${spacingVar("0-5")};
          padding-top: ${spacingVar("0-5")};
          padding-bottom: ${spacingVar("0-5")};
        }
        :host > button .logo {
          width: ${spacingVar("5")};
          height: ${spacingVar("5")};
        }
        :host > button > span,
        :host > button > dil-expand-down-icon {
          display: none;
        }
      }

      ul {
        display: flex;
        flex-direction: column;
        padding-top: ${spacingVar("3")};
        padding-left: ${spacingVar("2")};
        padding-right: ${spacingVar("2")};
        padding-bottom: 0;
        margin: 0;
      }

      li {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        box-shadow: 0px 1px 0px ${semanticVar("color-ui-divider-default")};
      }

      li button {
        border: none;
        background: none;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: ${spacingVar("2")};
        font: inherit;
        text-align: left;
        padding-left: ${spacingVar("2")};
        padding-right: ${spacingVar("2")};
        padding-top: ${spacingVar("1-5")};
        padding-bottom: ${spacingVar("1-5")};
        text-decoration: none;
        color: inherit;
      }

      .logo {
        width: ${semanticVar("icon-size-2xl")};
        height: ${semanticVar("icon-size-2xl")};
        border-radius: ${semanticVar("radius-lg")};
        object-fit: contain;
      }
      .logo.placeholder {
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${coreVar("color-gray-100")};
      }
      li button span {
        flex-grow: 1;
      }

      li button:not(:disabled) {
        cursor: pointer;
      }
      li button:not(:disabled):focus {
        outline-style: solid;
        outline-width: ${semanticVar("border-width-focus-width")};
        outline-color: ${semanticVar("color-ui-focus-ring")};
        outline-offset: calc(-2 * ${semanticVar("border-width-focus-offset")});
      }
      li button:not(:disabled):hover {
        background-color: var(--lens-component-button-tertiary-hover-background);
      }
      li button:not(:disabled):active {
        background-color: var(--lens-component-button-tertiary-active-background);
      }

      .no-results {
        padding: ${spacingVar("3")};
        color: ${semanticVar("color-type-muted")};
        text-align: center;
      }

      input[type="search"] {
        font: inherit;
        font-weight: ${semanticVar("font-weight-regular")};
        background: ${coreVar("color-gray-0")};
        border: 1px solid ${coreVar("color-gray-700")};
        border-radius: ${semanticVar("radius-md")};
        width: 100%;
        height: ${spacingVar("5")};
        padding: ${spacingVar("1) var(--lens-core-spacing-2")};
        padding-left: ${spacingVar("5")};
        -webkit-tap-highlight-color: transparent;
      }
      input[type="search"]:focus {
        outline-style: solid;
        outline-width: ${semanticVar("border-width-focus-width")};
        outline-color: ${semanticVar("color-ui-focus-ring")};
        outline-offset: ${semanticVar("border-width-focus-offset")};
      }
      input[type="search"]:hover {
        border-color: ${coreVar("color-gray-800")};
      }
      input[type="search"]:active {
        border-color: ${coreVar("color-gray-1000")};
      }

      @keyframes slide-up {
        from {
          opacity: 0;
          transform: translateY(${spacingVar("2")});
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes fade-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
    `,
];
export const ORG_SWITCHER_TAG = "atlas-gn-org-switcher";
defineComponent(ORG_SWITCHER_TAG, OrgSwitcher);
